import ServiceBase from "@/services/ServiceBase";
import { encryptValue, decryptValue } from "@/services/ServiceEncrypt";

import { useServiceStore } from '@/stores/serviceStore';

import { OAuth2Client, OAuth2Fetch } from '@badgateway/oauth2-client';
import { generateCodeVerifier } from '@badgateway/oauth2-client';

const IAM_URL = import.meta.env.IAM_URL;
const IAM_REALM = import.meta.env.IAM_REALM;
const IAM_CLIENT_ID = import.meta.env.IAM_CLIENT_ID;
const IAM_CLIENT_SECRET = import.meta.env.IAM_CLIENT_SECRET;
const IAM_CHECK_SSO_TIMEOUT = import.meta.env.IAM_CHECK_SSO_TIMEOUT || 5000;
const IAM_CHECK_SSO_CLIENT_ID = import.meta.env.IAM_CHECK_SSO_CLIENT_ID || "cartes-gouv-public";
const IAM_ENTREPOT_API_URL = import.meta.env.IAM_ENTREPOT_API_URL;

const SESSION_EXPIRED_SILENT_LOGOUT_DELAY_MS = 12 * 60 * 60 * 1000;

const OAUTH_PKCE_STORAGE_KEY = "oauth2:pkce";
const OAUTH_STATE_STORAGE_KEY = "oauth2:state";
const OAUTH_CALLBACK_URL_STORAGE_KEY = "oauth2:callback-url";

function buildLogoutRedirectUri (url, session) {
  const redirectUrl = new URL(url);
  redirectUrl.searchParams.set("session_status", session);
  return encodeURIComponent(redirectUrl.toString());
}

function buildOAuthState () {
  if (window.crypto && window.crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

class ServiceLocal extends ServiceBase {

  #client
  #fetchWrapper

  constructor(options) {
    super(options);

    /** state session */
    this.session = options.session || "";
    /** code verificated */
    this.codeVerifier = options.codeVerifier || "";
    /** code */
    this.code = options.code || "";
    /** token */
    this.token = options.token || "";
    /** erreurs */
    this.error = options.error || {};

    // variables à instancier !
    this.#client = null;
    this.#fetchWrapper = null;

    this.api = IAM_ENTREPOT_API_URL;
    
    this.#initialize(options);
    
    return this;
  }
  
  /**
   * Initialisation du client oauth
  */
  #initialize (options) {
    var settings = options.client ? options.client.settings : {
      server: `${IAM_URL}`,
  
      clientId: `${IAM_CLIENT_ID}`,
      clientSecret: `${IAM_CLIENT_SECRET}`,
      index: `${IAM_REALM}`,
  
      tokenEndpoint: `/realms/${IAM_REALM}/protocol/openid-connect/token`,
      authorizationEndpoint: `/realms/${IAM_REALM}/protocol/openid-connect/auth`,
      discoveryEndpoint: `/realms/${IAM_REALM}/.well-known/openid-configuration`,
      revocationEndpoint: `/realms/${IAM_REALM}/protocol/openid-connect/revoke`
    };
  
    this.#client = new OAuth2Client(settings);

    this.#fetchWrapper = new OAuth2Fetch({
      client: this.#client,
      scheduleRefresh: true,
      getNewToken: async () => {
        // en mode distant, on ne redemande pas de jeton
        if (this.token && Object.keys(this.token).length && this.token.expiresAt > Date.now()) {
          return this.token;
        }
        var token = await this.#client.authorizationCode.getToken({
          code: this.code,
          redirectUri: this.url,
          code_verifier: this.codeVerifier
        });
        return token;
      },
      storeToken: (token) => {
        this.token = token;
      },
      getStoredToken: () => {
        const token = this.#getTokenStorage();
        if (token && Object.keys(token).length) {
          return token;
        }
        return null;
      },
      onError: (e) => {
        console.error(e);
        this.error = e;
      }
    });

    this.setFetch(this.#fetchWrapper.fetch.bind(this.#fetchWrapper));
  }

  /**
   * Retourne le token d'authentification du localStorage
   * @returns {Object} auth
   * @example
   * {
   *    "authenticator": "authenticator:oauth2",
   *    "access_token": "phwt5hfbfzequdvrl9uaqfblucocgdlm62wqvom6",
   *    "expires_in": 3600,
   *    "token_type": "Bearer",
   *    "scope": "basic",
   *    "refresh_token": "t7vni7a8nptovlbzfl4et15wfmvp9knja0y1fe5v",
   *    "expires_at": 1730393104613
   * }
   */
  #getTokenStorage () {
    var service = JSON.parse(localStorage.getItem("service"));
    if (service) {
      return service.connexion.token;
    }
    return null;
  }

  /**
   * Verifie la session Keycloak en utilisant (iframe)
   * - soit via la méthode native/oauth2 (spécifique au local)
   * - soit via l'implémentation de ServiceBase (keycloak par défaut)
   * @param {String} adapter - natif | oauth2 | keycloak (par defaut)
   * @returns {Promise<Boolean>} - true if session is active, false otherwise
   */
  async checkKeycloakSession (adapter) {
    if (adapter !== "keycloak") {
      return this.#checkKeycloakSessionAdapterOAuth();
    }

    // Delegate keycloak (et fallback) au comportement par defaut
    // de ServiceBase.
    return super.checkKeycloakSession('keycloak');
  }

  /**
   * Experimental !
   * Adapter utilisant une approche plus "native" pour vérifier la session SSO via OAuth2
   * - Avantages : plus léger, pas besoin de charger la librairie Keycloak
   * - Inconvénients : moins robuste, peut ne pas gérer correctement tous les cas de figure (session expirée, rafraîchissement de token, etc.)
   * @returns 
   */
  async #checkKeycloakSessionAdapterOAuth () {
    console.log("use checkKeycloakSessionAdapter oauth");
    return new Promise((resolve) => {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        
        const checkUrl = new URL(this.#client.settings.server + this.#client.settings.authorizationEndpoint);
        checkUrl.searchParams.set('client_id', IAM_CHECK_SSO_CLIENT_ID);
        checkUrl.searchParams.set('redirect_uri', this.url + '/silent-check-sso-oauth.html');
        checkUrl.searchParams.set('response_type', 'code');
        checkUrl.searchParams.set('scope', 'openid');
        // INFO
        // 'prompt=none' is required for silent authentication; 
        // it prevents the OAuth2 server from showing any user interaction prompts in the iframe and
        // instead immediately returns an error if the user is not already authenticated.
        checkUrl.searchParams.set('prompt', 'none');
        
        iframe.src = checkUrl.toString();
        document.body.appendChild(iframe);
        
        const timeout = setTimeout(() => {
          cleanup();
          resolve(false); // Timeout = pas de session
        }, IAM_CHECK_SSO_TIMEOUT);
        
        function cleanup() {
          clearTimeout(timeout);
          window.removeEventListener('message', handleMessage);
          iframe.remove();
        }
        
        function handleMessage(event) {
          if (event.origin !== window.location.origin) return;
          
          cleanup();
          
          if (event.data.code) {
            // Session Keycloak active !
            resolve(true);
          } else {
            // Pas de session
            resolve(false);
          }
        }
        
        window.addEventListener('message', handleMessage);
    });
  }

  async resolveAccessStatus () {
    const emitter = this.getEmitter ? this.getEmitter() : null;
    const store = useServiceStore();

    // si IAM, on récupère les informations dans l'url
    const urlParams = new URLSearchParams(location.search);
    // parametres
    const code = urlParams.get('code');
    const session = urlParams.get('session_state') || urlParams.get('session_status');
    const error = urlParams.get('error');
    const from = urlParams.get('from');
    let status = "no-auth";

    // On traite l'erreur IAM en priorité.
    if (error) {
      this.error = {
        name: error,
        message: urlParams.get('error_description')
      };
      throw this.error;
    }

    // IAM login local
    if (code && session) {
      this.session = session;
      this.code = code;
      this.authenticated = true;
      status = "login";

      // Snapshot OAuth callback params to avoid races with router redirects
      // that can remove code/state from window.location before token exchange.
      const callbackUrlSnapshot = new URL(location.href);
      const callbackState = urlParams.get('state');
      if (callbackState && !callbackUrlSnapshot.searchParams.get('state')) {
        callbackUrlSnapshot.searchParams.set('state', callbackState);
      }

      try {
        const token = await this.getAccessToken({
          callbackUrl: callbackUrlSnapshot,
          state: callbackState,
          code,
          session
        });
        if (!token) {
          throw new Error('Token unavailable');
        }

        let user;
        try {
          user = await this.getUserMe();
        } catch (e) {
          throw new Error('Error to get user info (' + e.message + ')');
        }

        if (!user) {
          throw new Error('User profile unavailable from API');
        }

        console.debug(user);
        if (emitter) {
          emitter.dispatchEvent("service:user:loaded", {
            bubbles : true,
            detail : user
          });
        }

        try {
          const documents = await this.getDocuments();
          if (emitter) {
            emitter.dispatchEvent("service:documents:loaded", {
              bubbles : true,
              detail : documents
            });
          }
        } catch (e) {
          throw new Error('Error to get documents (' + e.message + ')');
        }

        // on enregistre le statut une fois toutes les données chargées
        store.setService(this);
        return status;
      } catch (e) {
        this.authenticated = false;
        throw new Error('Error to get token (' + e.message + ')');
      }
    }

    // IAM logout local
    // Logout.vue consomme session_state/session_status puis redirige vers '/' avec from=logout,
    // donc "session" est ici toujours null pour ce cas : la détection repose sur from === 'logout'.
    // La condition "session === this.session" est volontairement exclue : elle produisait
    // un faux positif (null === null) quand aucune session n'était en cours.
    if (!code && (session !== null || from === 'logout')) {
      this.session = null;
      this.code = null;
      this.authenticated = false;
      this.token = null;
      this.user = {};
      this.documents = {};
      this.error = {};
      status = "logout";
    }

    // enregistrement dans le storage du statut de la connexion
    // (uniquement pour les cas logout et no-auth, le login est géré dans la chaîne de promises)
    if (status !== "login") {
      store.setService(this);
    }

    return status;
  }

  /** 
   * IAM pour se connecter
   * 
   * @type {Promise}
   * @see Login
   * @example
   * // requête :
   * https://sso.geopf.fr/realms/geoplateforme/protocol/openid-connect/auth?
   *  scope=openid%20profile%20email&
   *  response_type=code&
   *  approval_prompt=auto&
   *  redirect_uri=http%3A%2F%2Flocalhost%3A5173%2Fcartes.gouv.fr-entree-carto/login&
   *  client_id=IAM_CLIENT_ID
   * // réponse avec redirection :
   * http://localhost:5173/cartes.gouv.fr-entree-carto/login?
   *  session_state=968321a6-385e-4058-a17a-571ab08303bd&
   *  iss=https%3A%2F%2Fsso.geopf.fr%2Frealms%2Fgeoplateforme&
   *  code=168e7dd8-ae1f-4f8b-99f7-ae0aac066067.968321a6-385e-4058-a17a-571ab08303bd.3038d336-2dfa-4c2e-954e-090ee781ed7f
   * 
   */
  async getAccessLogin () {
    // INFO
    // Dans le mode auth local
    // La réponse fournit le 'code', 
    // et il doit être utiliser pour obtenir le token 
    // cf. getAccessToken()

    const url = this.url.includes("login") ? this.url : this.url + "/login";
      
    const codeVerifier = await generateCodeVerifier();
    const state = buildOAuthState();

    this.codeVerifier = codeVerifier;
    const encryptedState = await encryptValue(state);
    sessionStorage.setItem(`${OAUTH_PKCE_STORAGE_KEY}:${encryptedState}`, codeVerifier);
    sessionStorage.setItem(OAUTH_STATE_STORAGE_KEY, encryptedState);
    // Compatibilite avec les anciennes sessions/login en cours.
    localStorage.setItem("codeVerifier", codeVerifier);
    
    var responseIAM = await this.#client.authorizationCode.getAuthorizeUri({
      redirectUri: url,
      state,
      codeVerifier,
      scope: ['openid','profile','email'],
      extraParams: {
        approval_prompt: "auto"
      },
      responseMode: "query"
    });

    return responseIAM;
  }

  /** 
   * IAM pour se deconnecter
   * 
   * @type {Promise}
   * @see Logout
   * @example
   * // requête :
   * https://sso.geopf.fr/realms/geoplateforme/protocol/openid-connect/logout?
   *  post_logout_redirect_uri=http%3A%2F%2Flocalhost%3A5173%2Fcartes.gouv.fr-entree-carto/logout&
   *  scope=profile%20email&
   *  response_type=code&
   *  approval_prompt=auto&
   *  client_id=IAM_CLIENT_ID
   * // réponse avec redirection :
   * http://localhost:5173/cartes.gouv.fr-entree-carto/?
   *  session_state=968321a6-385e-4058-a17a-571ab08303bd
   */
  async getAccessLogout () {
    // INFO
    // La reponse fournit la 'session',
    // et la session doit être identique à celle issue de login

    const url = this.url.includes("logout") ? this.url : this.url + "/logout";

    var responseIAM = `${this.#client.settings.server}/realms/${this.#client.settings.index}/protocol/openid-connect/logout?
      scope=openid%20profile%20email&
      approval_prompt=auto&
      response_type=code&
      client_id=${this.#client.settings.clientId}&
      post_logout_redirect_uri=${buildLogoutRedirectUri(url, this.session)}`.replace(/ /g, '');

    return Promise.resolve(responseIAM);
  }

  /**
   * IAM pour se deconnecter silencieusement
   * 
   * @type {Promise}
   * @see Logout
   * @example
   * GET /realms/{realm}/protocol/openid-connect/logout?
   *   id_token_hint=<id_token>
   *   &post_logout_redirect_uri=https://myapp.com/loggedout
   *   &client_id=my-client
   */
  async getAccessLogoutSilent () {
    const url = this.url.includes("logout") ? this.url : this.url + "/logout";

    if (!this.token || !this.token.idToken) {
      return Promise.reject(new Error('No ID token available for silent logout'));
    }
    
    
    var responseIAM = `${this.#client.settings.server}/realms/${this.#client.settings.index}/protocol/openid-connect/logout?
      id_token_hint=${this.token.idToken}&
      client_id=${this.#client.settings.clientId}&
      post_logout_redirect_uri=${buildLogoutRedirectUri(url, this.session)}`.replace(/ /g, '');

    return Promise.resolve(responseIAM);
  } 
  
  /** 
   * IAM pour obtenir le token
   * 
   * @type {Promise}
   * @see resolveAccessStatus
   * @example
   * POST https://sso.geopf.fr/realms/geoplateforme/protocol/openid-connect/token
   * content-type: application/x-www-form-urlencoded
   * data : {
   *   "grant_type": "authorization_code",
   *   "code": "558c7a7d-40f6-4bef-b326-445238e6e594.77d96aef-44c3-42fb-a542-c1d0a521ce29.c24814cd-85a8-4e3c-919f-22664ec080cc",
   *   "redirect_uri": "http://localhost/redirect",
   *   "client_id": "...",
   *   "client_secret": "..."
   * }
   * // réponse :
   * {
   *    "access_token": "...",
   *    "token_type": "Bearer",
   *    "expires_in": 43200,
   *    "session_state": "77d96aef-44c3-42fb-a542-c1d0a521ce29",
   *    ...
   * }
  */
  async getAccessToken (options = {}) {
    const { callbackUrl, state, code, session } = options;
    const url = this.url.includes("login") ? this.url : this.url + "/login";
    let resolvedCallbackUrl = callbackUrl
      ? new URL(callbackUrl.toString())
      : new URL(location.href);

    const storedCallbackUrlRaw = sessionStorage.getItem(OAUTH_CALLBACK_URL_STORAGE_KEY);
    if ((!code || !resolvedCallbackUrl.searchParams.get('code')) && storedCallbackUrlRaw) {
      try {
        resolvedCallbackUrl = new URL(storedCallbackUrlRaw);
      } catch {
        sessionStorage.removeItem(OAUTH_CALLBACK_URL_STORAGE_KEY);
      }
    }

    const resolvedCode = code || this.code || resolvedCallbackUrl.searchParams.get('code');
    const resolvedSession = session || this.session || resolvedCallbackUrl.searchParams.get('session_state');
    const resolvedStateOption = state || resolvedCallbackUrl.searchParams.get('state');

    if (resolvedCode && !resolvedCallbackUrl.searchParams.get('code')) {
      resolvedCallbackUrl.searchParams.set('code', resolvedCode);
    }
    if (resolvedSession && !resolvedCallbackUrl.searchParams.get('session_state')) {
      resolvedCallbackUrl.searchParams.set('session_state', resolvedSession);
    }
    if (resolvedStateOption && !resolvedCallbackUrl.searchParams.get('state')) {
      resolvedCallbackUrl.searchParams.set('state', resolvedStateOption);
    }

    const urlParams = new URLSearchParams(resolvedCallbackUrl.search);
    const stateFromRedirect = urlParams.get('state');
    const storedState = sessionStorage.getItem(OAUTH_STATE_STORAGE_KEY);
    const storedStateDecrypted = storedState ? await decryptValue(storedState) : null;
    
    if (stateFromRedirect && storedStateDecrypted && stateFromRedirect !== storedStateDecrypted) {
      throw new Error('OAuth state mismatch');
    }

    const expectedState = stateFromRedirect || storedStateDecrypted;
    const codeVerifierFromState = expectedState
      ? sessionStorage.getItem(`${OAUTH_PKCE_STORAGE_KEY}:${expectedState}`)
      : null;
      
    var codeVerifier = this.codeVerifier || codeVerifierFromState || localStorage.getItem("codeVerifier");

    if (!codeVerifier) {
      throw new Error('Missing PKCE code verifier');
    }

    if (!resolvedCallbackUrl.searchParams.get('code')) {
      throw new Error('Missing authorization code in callback URL');
    }
    
    var token = await this.#client.authorizationCode.getTokenFromCodeRedirect(
      resolvedCallbackUrl,
      {
        redirectUri: url,
        state: expectedState || undefined,
        codeVerifier,
      }
    );
    this.token = token;
  
    const today = new Date(token.expiresAt);
    console.debug("expires token", today);

    this.#fetchWrapper.token = token; // HACK !?

    if (storedState) {
      sessionStorage.removeItem(`${OAUTH_PKCE_STORAGE_KEY}:${storedState}`);
    }
    sessionStorage.removeItem(OAUTH_STATE_STORAGE_KEY);
    sessionStorage.removeItem(OAUTH_CALLBACK_URL_STORAGE_KEY);
    localStorage.removeItem("codeVerifier");

    var store = useServiceStore();
    store.setService(this);

    return this.token;
  }

  /**
   * Not used !
   * Indique si le token est expiré depuis plus de 12h.
   * @description
   * Cette vérification devait être utilisée pour déclencher une déconnexion 
   * silencieuse lorsque le token est expiré depuis plus de 12 heures.
   * Mais, le nouveau workflow de deconnexion ne se base plus sur cette logique...
   * @returns {Boolean}
   */
  isTokenExpiredForMoreThan12h () {
    if (!this.token || !this.token.expiresAt) {
      return true;
    }

    const expiresAt = Number(this.token.expiresAt);

    if (!Number.isFinite(expiresAt)) {
      return false;
    }

    return (Date.now() - expiresAt) >= SESSION_EXPIRED_SILENT_LOGOUT_DELAY_MS;
  }
}

export default ServiceLocal;